import React, {
  useReducer,
  useEffect,
  useState,
  useCallback,
  createContext,
  useRef,
  useMemo,
  useContext
} from 'react';
import generateId from '../../../utils/generateId';
import { AutomationsContex } from '../../engine/FormBuilderAutomationsEngine';
import { FormBuilderContext } from '../../engine/FormBuilderEngine';
import ConditionsQueryBuilderView from '../view/ConditionsQueryBuilderView';
import { Options } from '../view/ConditionsQueryBuilderView';

export const ConditionsQueryBuilderContext = createContext({});

const ConditionsQueryReducer = (state, action) => {
  const { type, payload, id } = action;

  switch (type) {
    case 'load':
      return { ...payload };
    case 'update':
      return { ...state, [id]: payload };
    default:
      return state;
  }
};

export const EMPTY_QUERY_BLOCK = { field: '', op: '', value: '' };
export const EMPTY_BASE_QUERY = [EMPTY_QUERY_BLOCK];
// const mock =
//   '[[{"field":"${f1995598612609344}","op":"<>","value":"","internOp":"or"}],[{"field":"${f3780033801906048}","op":"ILIKE","value":"%||http://google.com||%","internOp":"or"},{"field":"${f3780033801906048}","op":"ILIKE","value":"%||[I\'m an inline-style link](https://www.google.com)||%","internOp":"or"}],[{"field":"${f8909559474044176}","op":"=","value":1620203040000,"internOp":"or"}],[{"field":"${f9792690748211008}","op":"=","value":"Argentina","internOp":"or"}],[{"field":"${f9805620952766736}","op":"=","value":"Yes","internOp":"or"}],[{"field":"${f0154130087409168}","op":"=","value":"851","internOp":"or"}]]';

/**
 *
 *  This function decides whether a condition component could have a "-" button
 *  to delete itself or if the component already takes care of it self and the "-"
 *  button is defined within it.
 *  We could say that each component should take care of itself and avoid the small
 *  complication of understaning this but the amount of overhead required for
 *  addining this functionality isn't small (handling animation delete sequence).
 *  So buttom line we can use this function to manage some of components deletion
 *  with the same code (great for future new conditions we'd like to add).
 *
 * @param {Options (String)} type - type of condition component
 * @returns true if the component is handling removal of itself or false if it should get handled in the top level
 */
const isHandlingSelf = type => {
  switch (type) {
    case Options.STATUS:
    case Options.ASSIGNED_USER:
    case Options.RESPONSIBLE_ADMIN:
      return true;
    case Options.FIELDS:
    case Options.FLAGGED:
    case Options.DATE_RANGE:
    default:
      return false;
  }
};

const getQueryType = input => {
  const getTypeFromString = inp => {
    if (inp.includes(Options.FIELDS)) {
      return Options.FIELDS;
    }
    if (inp.includes(Options.FLAGGED)) {
      return Options.FLAGGED;
    }
    if (inp.includes(Options.STATUS)) {
      return Options.STATUS;
    }
    if (inp.includes(Options.ASSIGNED_USER)) {
      return Options.ASSIGNED_USER;
    }
    if (inp.includes(Options.RESPONSIBLE_ADMIN)) {
      return Options.RESPONSIBLE_ADMIN;
    }
    if (inp.includes(Options.DATE_RANGE)) {
      return Options.DATE_RANGE;
    }
    return '';
  };
  const getTypeFromObject = ([inp]) => {
    if (inp.field.includes('${')) {
      return Options.FIELDS;
    }
    if (inp.field.includes('Date')) {
      return Options.DATE_RANGE;
    }
    if (inp.field.includes('flagged')) {
      return Options.FLAGGED;
    }
    if (inp.field.includes('status')) {
      return Options.STATUS;
    }
    if (inp.field.includes('submitterId')) {
      return Options.ASSIGNED_USER;
    }
    if (inp.field.includes('assigneeId')) {
      return Options.RESPONSIBLE_ADMIN;
    }
    return '';
  };

  if (typeof input === 'string') {
    return getTypeFromString(input);
  } else {
    return getTypeFromObject(input);
  }
};

export const trimConditions = queryArr => {
  return queryArr.filter(
    qBlock =>
      !!qBlock.op &&
      qBlock.value !== null &&
      !!qBlock.field &&
      !!qBlock.internOp
  );
};

const parseQuery = (originalQueries, queryOp) => {
  const queries = JSON.parse(JSON.stringify(originalQueries));
  // console.log(`queries`, queries);

  Object.entries(queries).forEach(([key, queryArr]) => {
    if (getQueryType(key) === Options.FIELDS) {
      const trimmedQuery = trimConditions(queryArr);
      if (!!!trimmedQuery.length) {
        delete queries[key];
      }
    }
  });

  const results = Object.values(queries);

  if (queryOp === 'or' && results.length) {
    results.forEach((r, index) => {
      if (index > 0) {
        r.push({op: 'OR'})
      }
    })
  }

  // console.log(`queries`, queries);

  return JSON.stringify(results);
};

const cleanIncomingConditions = conditions => {
  let op = 'and';
  if (!conditions || !conditions.length || conditions.length < 2) {
    return op;
  }

  const firstCond = conditions[1];
  const potentialQueryOpNode = firstCond[firstCond.length - 1];
  if (potentialQueryOpNode && Object.keys(potentialQueryOpNode).length === 1) {
    // not a query block - has to be operator block -> {op: "OR"})
    op = potentialQueryOpNode.op; // should be 'or'
    if (op.toLowerCase() === 'or') {
      conditions.forEach(c => {
        const lastNode = c[c.length -1];
        if (lastNode.op === op) {
          c.pop();
        }
      }); // remove operator block from all conditions
    }
  }
  return op;
};

/**
 * *Note in the future we might want to add more views
 *
 * @param {String} backEndConditions - Stringified object in a format used in the backend
 * @param {Function} update - After every update - this function is going to get called with the updated query
 * @param {Object} form - the form schema
 * @param {Boolean} useSpacers -
 * @param {String} variant - which variant to use for the select/input components (SelectEngine/InputBase).
 *                            **IMPORTANT** if you're going to need the select/input to extend to the end of the
 *                            row please look at FieldsQueryBuilderView -> Container div. Should add the variant
 *                            name to wideConditionValueContainerVariantList map in order for it to work with full width (little messy - sorry).
 * @param {Array} filter - From ConditionsQueryBuilderView-> Options, select options you want to not show on the drop down of conditions
 * @param {String} condtionsPrefixText - specify text to add before the first condition
 * @param {String} addConditionLabelText - alternate text for the Add Condition button (default is 'Add a condition')
 *  */
const ConditionsQueryBuilderEngine = ({
  backEndConditions = '[]',
  // backEndConditions = mock,
  update = () => {},
  form: propsForm = {},
  useSpacers,
  variant,
  filter = [],
  condtionsPrefixText,
  addConditionLabelText
}) => {
  const [queries, dispatch] = useReducer(ConditionsQueryReducer, {});
  const [queryOp, setQueryOp] = useState('and'); // What type of operator we should use between the queries - by default
  // there is no indication in the query which means it's "and" (&&).
  const [anchorBtn, setAnchorBtn] = useState(null);
  const [hoverOn, setHoverOn] = useState(null);
  const [inDeleteSequence, setInDeleteSequence] = useState(null);
  const skipRender = useRef(true);
  const { form: fbForm } = useContext(FormBuilderContext);
  const { form: automationsForm } = useContext(AutomationsContex);

  const form = useMemo(() => {
    if (Object.keys(propsForm).length) {
      return propsForm;
    }
    if (Object.keys(fbForm).length) {
      return fbForm;
    }
    if (Object.keys(automationsForm).length) {
      return automationsForm;
    }
  }, [propsForm]);

  useEffect(() => {
    if (skipRender.current) {
      skipRender.current = false;
      return;
    }

    update(parseQuery(queries, queryOp));
  }, [queries, queryOp]);

  useEffect(() => {
    const parsedConditions =
      typeof backEndConditions === 'string'
        ? JSON.parse(backEndConditions)
        : backEndConditions; // try catch?
    const state = {};

    const op = cleanIncomingConditions(parsedConditions);
    setQueryOp((op || '').toLocaleLowerCase()); // should never be ''

    parsedConditions.forEach(qBlock => {
      if (!Array.isArray(qBlock)) {
        return;
      }
      const key = getQueryType(qBlock);
      state[`${key}-${generateId()}`] = qBlock; // we use string as key to keep order
    });
    skipRender.current = true;
    dispatch({ type: 'load', payload: state });
  }, []);

  const onMenuClose = useCallback(() => {
    setAnchorBtn(null);
  }, []);

  const onMenuOpen = useCallback(e => {
    setAnchorBtn(e.currentTarget);
  }, []);

  const onMenuSelect = useCallback(
    opt => {
      const queriesCpy = JSON.parse(JSON.stringify(queries));
      queriesCpy[`${opt}-${generateId()}`] = EMPTY_BASE_QUERY;
      skipRender.current = true;
      dispatch({ type: 'load', payload: queriesCpy });
      setAnchorBtn(null);
    },
    [queries]
  );

  const removeCondition = useCallback(
    id => {
      const queriesCpy = JSON.parse(JSON.stringify(queries));
      delete queriesCpy[id];
      dispatch({ type: 'load', payload: queriesCpy });
      setInDeleteSequence(null);
    },
    [queries]
  );

  const updateQuery = useCallback((id, qBlock) => {
    if (!!!qBlock.length) {
      setInDeleteSequence(id);
      return;
    }
    dispatch({ type: 'update', payload: qBlock, id });
  }, []);

  // const updateFieldQuery = useCallback(
  //   (id, qBlock) => {
  //     const actualQBlock = qBlock[0];
  //     if (
  //       actualQBlock.conditions &&
  //       !!Object.keys(actualQBlock.conditions).length
  //     ) {
  //       updateQuery(id, qBlock);
  //     } else {
  //       removeCondition(id);
  //     }
  //   },
  //   [updateQuery, removeCondition]
  // );

  const contextProps = { form, variant };

  const viewProps = {
    queries,
    filter,
    onMenuOpen,
    onMenuClose,
    onMenuSelect,
    anchorBtn,
    queryOp,
    setQueryOp,
    setHoverOn,
    hoverOn,
    removeCondition,
    setInDeleteSequence,
    inDeleteSequence,
    // updateFieldQuery,
    updateQuery,
    getQueryType,
    useSpacers,
    isHandlingSelf,
    condtionsPrefixText,
    addConditionLabelText,
    form
  };

  return (
    <ConditionsQueryBuilderContext.Provider value={contextProps}>
      <ConditionsQueryBuilderView {...viewProps} />
    </ConditionsQueryBuilderContext.Provider>
  );
};

export default ConditionsQueryBuilderEngine;
